/**
 * Calendar type chips and vacation-zone select.
 */
import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  CALENDAR_FILTER_TOGGLES,
  EVENT_TYPE_CONFIG,
  type CalendarFilterToggle,
} from '@/components/calendar/calendar-configs';
import { buildCalendarZoneOptions, useEventTypeIcons } from '@/components/calendar/calendar-utils';
import { Select } from '@/components/forms/Select';
import { getVacationZones } from '@/services/holidays.service';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import type { CalendarEventType } from '@/types/calendar.types';
import type { ElementWidth } from '@/types/interface-elements.types';
import { cn } from '@/lib/utils';

/** Props for {@link FilterToggle}. */
interface FilterToggleProps {
  label: string;
  name: string;
  icon: React.ReactNode;
  isActive: boolean;
  onClick: () => void;
  filterColor: string;
  showLabel: boolean;
}

/** Session-filter badge token (`--filter-color`). */
function filterBadgeStyle(filterColor: string): React.CSSProperties {
  return { '--filter-color': filterColor } as React.CSSProperties;
}

/** Type-visibility chip: same badge tokens as session filters (`--filter-color`). */
const FilterToggle: React.FC<Readonly<FilterToggleProps>> = ({
  label,
  name,
  icon,
  isActive,
  onClick,
  filterColor,
  showLabel,
}) => (
  <TooltipProvider delayDuration={200}>
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={onClick}
          className={cn(
            'calendar-filter-toggle',
            showLabel && 'calendar-filter-toggle-labeled',
            isActive ? 'filter-badge-active' : 'filter-badge-inactive',
          )}
          style={filterBadgeStyle(filterColor)}
        >
          {icon}
          {showLabel ? <span className="calendar-filter-toggle-name">{name}</span> : null}
        </button>
      </TooltipTrigger>
      <TooltipContent>
        <p>{isActive ? `Masquer ${label}` : `Afficher ${label}`}</p>
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

/** Props for {@link CalendarFilters}. */
interface CalendarFiltersProps {
  /** Persisted vacation-zone id, or empty for every zone. */
  zoneValue?: string;
  onZoneChange: (value: string) => void;
  activeTypes: readonly CalendarEventType[];
  onToggleType: (type: CalendarEventType) => void;
  /** Dashboard section width; labels show on `full` and `2/3`. */
  layoutWidth?: ElementWidth;
  showFieldEvents: boolean;
}

/** Vacation-zone select and event-type chips. */
export const CalendarFilters: React.FC<Readonly<CalendarFiltersProps>> = ({
  zoneValue,
  onZoneChange,
  activeTypes,
  onToggleType,
  layoutWidth = 'full',
  showFieldEvents,
}) => {
  const typeIcons = useEventTypeIcons();
  const showFilterLabels = layoutWidth === 'full' || layoutWidth === '2/3';
  const filterToggles = CALENDAR_FILTER_TOGGLES.filter(
    (toggle) => showFieldEvents || toggle.type !== 'event',
  );

  const { data: vacationZones = [] } = useQuery({
    queryKey: ['vacation-zones'],
    queryFn: () => getVacationZones(),
  });
  const calendarZoneOptions = React.useMemo(
    () => buildCalendarZoneOptions(vacationZones),
    [vacationZones],
  );

  function renderTypeToggle(toggle: CalendarFilterToggle): React.ReactNode {
    function handleToggle(): void {
      onToggleType(toggle.type);
    }

    const Icon = typeIcons[toggle.type];
    return (
      <FilterToggle
        key={toggle.type}
        label={toggle.label}
        name={EVENT_TYPE_CONFIG[toggle.type].label}
        icon={<Icon className="h-4 w-4" />}
        isActive={activeTypes.includes(toggle.type)}
        onClick={handleToggle}
        filterColor={EVENT_TYPE_CONFIG[toggle.type].color}
        showLabel={showFilterLabels}
      />
    );
  }

  return (
    <div className="calendar-filter-bar">
      <Select
        className="calendar-zone-filter"
        size="sm"
        label="Vacances scolaires"
        labelTooltip="Affiche les vacances nationales et celles de la zone choisie"
        value={zoneValue || ''}
        onChange={onZoneChange}
        options={calendarZoneOptions}
      />
      <div className="calendar-filter-toggles">{filterToggles.map(renderTypeToggle)}</div>
    </div>
  );
};
